import React from "react";
import { Box, Drawer } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { BUTTON_RESET, C, FONT_BODY, FOCUS_RING, RADIUS } from "../../../utils/designTokens";

/**
 * Narrow screens: a sticky "View cart · $total" bar, opening the cart as a
 * bottom sheet. The sheet stays mounted while closed, so the cart keeps
 * pricing its lines (CartView fills in each line's price) and the bar's total
 * is right before the sheet is ever opened.
 */
function CartSheet({ open, onOpen, onClose, count = 0, totalLabel, showBar = true, children }) {
  return (
    <>
      {showBar && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            bgcolor: C.bg,
            borderTop: `1px solid ${C.border}`,
            px: 2,
            pt: 1.5,
            pb: "calc(12px + env(safe-area-inset-bottom, 0px))",
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={onOpen}
            aria-haspopup="dialog"
            aria-expanded={open}
            sx={{
              ...BUTTON_RESET,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.25,
              width: "100%",
              maxWidth: 560,
              mx: "auto",
              height: 56,
              borderRadius: RADIUS.controlLg,
              bgcolor: C.red,
              color: "#FFFFFF",
              fontFamily: FONT_BODY,
              fontSize: 16,
              fontWeight: 700,
              transition: "background-color .15s ease",
              "&:hover": { bgcolor: C.redHover },
              ...FOCUS_RING,
            }}
          >
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                minWidth: 28,
                height: 28,
                px: 0.75,
                boxSizing: "border-box",
                borderRadius: RADIUS.pill,
                bgcolor: C.redDark,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {count}
            </Box>
            <span>{`View cart · ${totalLabel}`}</span>
          </Box>
        </Box>
      )}

      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          "aria-label": "Your order",
          sx: {
            borderTopLeftRadius: RADIUS.card,
            borderTopRightRadius: RADIUS.card,
            maxHeight: "92vh",
            maxWidth: 640,
            mx: "auto",
          },
        }}
      >
        <Box sx={{ position: "sticky", top: 0, zIndex: 1, display: "flex", justifyContent: "center", bgcolor: C.bg, pt: 1 }}>
          <Box aria-hidden="true" sx={{ width: 40, height: 4, borderRadius: RADIUS.pill, bgcolor: C.borderStrong }} />
          <Box
            component="button"
            type="button"
            onClick={onClose}
            aria-label="Close cart"
            sx={{
              ...BUTTON_RESET,
              position: "absolute",
              right: 8,
              top: 8,
              width: 44,
              height: 44,
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: C.ink,
              "&:hover": { bgcolor: C.surface2 },
              ...FOCUS_RING,
            }}
          >
            <CloseRoundedIcon />
          </Box>
        </Box>
        <Box sx={{ pb: "env(safe-area-inset-bottom, 0px)" }}>{children}</Box>
      </Drawer>
    </>
  );
}

export default React.memo(CartSheet);
